/**
 * RentArrearsBoard — Batch #14.231
 *
 * Tableau des loyers en retard (aging) : buckets 1-30 / 31-60 / 61+ jours
 * + liste des locataires en retard. Réutilisé :
 *   - Portail immeuble : endpoint /portal/immeuble/rent-arrears
 *   - Gestion admin (mirror) : endpoint /admin/pms/rent-arrears
 *
 * Props :
 *   endpoint   : URL complète de l'endpoint aging
 *   getHeaders : fonction retournant les headers auth
 *   testIdPrefix
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BarChart3, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Phone } from "lucide-react";
import { fmtCAD as money } from "@/lib/format";


const BUCKETS_META = [
  { key: "b1_30", label: "1-30 jours", bar: "bg-amber-400", text: "text-amber-700" },
  { key: "b31_60", label: "31-60 jours", bar: "bg-orange-500", text: "text-orange-700" },
  { key: "b61_plus", label: "61 jours +", bar: "bg-rose-500", text: "text-rose-700" },
];

export function RentArrearsBoard({ endpoint, getHeaders, testIdPrefix = "arrears" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(endpoint, { headers: getHeaders ? getHeaders() : {} });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [endpoint, getHeaders]);

  useEffect(() => { load(); }, [load]);

  const buckets = data?.buckets || {};
  const tenants = data?.tenants || [];
  const maxAmount = Math.max(1, ...BUCKETS_META.map((b) => buckets[b.key]?.amount || 0));

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 space-y-3"
      data-testid={`${testIdPrefix}-board`}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <span className="w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center">
            <BarChart3 className="w-4 h-4" />
          </span>
          Loyers en retard
          {data && data.overdue_tenants_count > 0 && (
            <span className="text-[10px] font-bold bg-rose-100 text-rose-700 rounded-full px-2 py-0.5"
                  data-testid={`${testIdPrefix}-board-count`}>
              {data.overdue_tenants_count} locataire{data.overdue_tenants_count > 1 ? "s" : ""}
            </span>
          )}
        </h3>
        <button
          onClick={load} disabled={loading}
          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 disabled:opacity-50"
          data-testid={`${testIdPrefix}-board-refresh`}
          title="Rafraîchir"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </div>

      {loading && !data ? (
        <div className="py-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : !data ? (
        <div className="py-4 text-center text-xs text-slate-400">Impossible de charger les retards.</div>
      ) : data.overdue_tenants_count === 0 ? (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5 text-xs text-emerald-700"
             data-testid={`${testIdPrefix}-board-empty`}>
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          Aucun loyer en retard — {data.tenants_ok_count} locataire{data.tenants_ok_count > 1 ? "s" : ""} à jour.
        </div>
      ) : (
        <>
          {/* Buckets */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-[11px]">
              <div className="w-20 text-slate-600 shrink-0 font-semibold">À jour</div>
              <div className="flex-1 text-emerald-600 font-semibold">{data.tenants_ok_count} locataire{data.tenants_ok_count > 1 ? "s" : ""}</div>
            </div>
            {BUCKETS_META.map((b) => {
              const v = buckets[b.key] || { count: 0, amount: 0 };
              return (
                <div key={b.key} className="flex items-center gap-2 text-[11px]">
                  <div className="w-20 text-slate-600 shrink-0">{b.label}</div>
                  <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                    <div
                      className={`h-full ${b.bar} rounded-full transition-all`}
                      style={{ width: `${Math.round((v.amount / maxAmount) * 100)}%` }}
                    />
                  </div>
                  <div className={`w-28 text-right shrink-0 ${v.count > 0 ? b.text + " font-semibold" : "text-slate-400"}`}>
                    {v.count} mois · {money(v.amount)}
                  </div>
                </div>
              );
            })}
            <div className="flex justify-end text-[11px] font-bold text-rose-700 pt-1 border-t border-slate-100">
              Total en souffrance : {money(data.total_overdue_amount)}
            </div>
          </div>

          {/* Liste des locataires en retard */}
          <div className="space-y-1.5" data-testid={`${testIdPrefix}-board-tenants`}>
            {tenants.map((t) => (
              <div key={t.tenant_id}
                   className="flex items-center gap-2.5 bg-rose-50/60 border border-rose-100 rounded-xl px-3 py-2"
                   data-testid={`${testIdPrefix}-board-tenant-${t.tenant_id}`}>
                <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-800 truncate">
                    {t.name}
                    {(t.unit_label || t.property_name) && (
                      <span className="font-normal text-slate-400">
                        {" "}· {[t.property_name, t.unit_label && `apt ${t.unit_label}`].filter(Boolean).join(", ")}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {t.overdue_count} mois impayé{t.overdue_count > 1 ? "s" : ""} ({t.periods.join(", ")}) · plus ancien : {t.oldest_days} j
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-bold text-rose-700">{money(t.overdue_amount)}</div>
                  {t.phone && (
                    <div className="text-[10px] text-slate-400 inline-flex items-center gap-0.5">
                      <Phone className="w-2.5 h-2.5" />{t.phone}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default RentArrearsBoard;
